import { GuidedTourContext, GuidedTourStep } from '../models/guide/guided-tour.model';
import { patientRoute, providerRoute } from './guided-tour-routes.util';

export const SEARCH_TOUR_STEPS: GuidedTourStep[] = [
  {
    target: 'search-header',
    title: 'Find healthcare providers',
    description: 'Search verified caregivers and doctors across the Philippines from this page.',
    route: '/search',
    placement: 'bottom',
  },
  {
    target: 'search-query',
    title: 'Search by keyword',
    description: 'Look up providers by name, service, specialty, or location keywords.',
    route: '/search',
    placement: 'bottom',
  },
  {
    target: 'search-location',
    title: 'Use your location',
    description: 'Allow location access to sort results by distance and filter by radius.',
    route: '/search',
    placement: 'bottom',
  },
  {
    target: 'search-filters',
    title: 'Refine results',
    description: 'Filter by service mode, catalog, provider type, minimum rating, and distance.',
    route: '/search',
    placement: 'right',
  },
  {
    target: 'search-sort',
    title: 'Sort and view',
    description: 'Sort by nearest provider or price, and switch between list and grid view.',
    route: '/search',
    placement: 'bottom',
  },
  {
    target: 'search-results',
    title: 'Open a provider profile',
    description: 'Select a result to view services, reviews, availability, and start booking.',
    route: '/search',
    placement: 'top',
  },
];

export function buildPatientFullTour(context: GuidedTourContext): GuidedTourStep[] {
  const patientGuid = context.patientGuid ?? null;
  const dashboard = patientRoute(patientGuid, 'dashboard');
  const bookings = patientRoute(patientGuid, 'bookings');
  const favorites = patientRoute(patientGuid, 'favorites');
  const pastVisits = patientRoute(patientGuid, 'past-visits');
  const profile = patientRoute(patientGuid, 'profile');

  return [
    {
      target: 'patient-profile',
      title: 'Patient dashboard',
      description: 'Your home base for bookings, profile, and quick actions.',
      route: dashboard,
      placement: 'bottom',
    },
    {
      target: 'patient-book-now',
      title: 'Book a provider',
      description: 'Jump straight to search when you are ready to book care.',
      route: dashboard,
      placement: 'left',
    },
    {
      target: 'patient-quick-actions',
      title: 'Quick shortcuts',
      description: 'Access search, bookings, favorites, and past visits in one click.',
      route: dashboard,
      placement: 'bottom',
    },
    {
      target: 'patient-upcoming',
      title: 'Upcoming appointments',
      description: 'See your next visits and open the full bookings list from here.',
      route: dashboard,
      placement: 'top',
    },
    ...SEARCH_TOUR_STEPS,
    {
      target: 'patient-bookings-header',
      title: 'My Bookings',
      description: 'Track every appointment from request through payment and completion.',
      route: bookings,
      placement: 'bottom',
    },
    {
      target: 'patient-bookings-filters',
      title: 'Booking filters',
      description: 'Filter by upcoming, completed, cancelled, or bookings that need payment.',
      route: bookings,
      placement: 'bottom',
    },
    {
      target: 'patient-bookings-list',
      title: 'Manage appointments',
      description: 'Pay, cancel, confirm delivery, leave reviews, and view booking details.',
      route: bookings,
      placement: 'top',
    },
    {
      target: 'patient-favorites-header',
      title: 'Saved providers',
      description: 'Keep your preferred caregivers and doctors in one place for faster rebooking.',
      route: favorites,
      placement: 'bottom',
    },
    {
      target: 'patient-favorites-list',
      title: 'Favorite providers list',
      description: 'Search within favorites and book again when you need care.',
      route: favorites,
      placement: 'top',
    },
    {
      target: 'patient-past-visits-header',
      title: 'Past visits',
      description: 'Review your completed care history and revisit providers you have seen before.',
      route: pastVisits,
      placement: 'bottom',
    },
    {
      target: 'patient-past-visits-list',
      title: 'Visit history',
      description: 'Browse completed visits and open details for each past appointment.',
      route: pastVisits,
      placement: 'top',
    },
    {
      target: 'patient-profile-header',
      title: 'Your profile',
      description: 'Update personal details, medical information, and account preferences.',
      route: profile,
      placement: 'bottom',
    },
    {
      target: 'patient-profile-photo',
      title: 'Profile photo',
      description: 'Upload a photo so providers can recognize you during visits.',
      route: profile,
      placement: 'right',
    },
  ];
}

export function buildProviderFullTour(context: GuidedTourContext): GuidedTourStep[] {
  const providerGuid = context.providerGuid ?? null;
  const isVerified = context.providerVerified ?? false;
  const dashboard = providerRoute(providerGuid, 'dashboard');
  const verification = providerRoute(providerGuid, 'verification');
  const services = providerRoute(providerGuid, 'services');
  const schedule = providerRoute(providerGuid, 'schedule');
  const bookings = providerRoute(providerGuid, 'bookings');
  const reviews = providerRoute(providerGuid, 'reviews');
  const payouts = providerRoute(providerGuid, 'payouts');
  const profile = providerRoute(providerGuid, 'profile');

  const steps: GuidedTourStep[] = [
    {
      target: 'provider-profile',
      title: 'Provider dashboard',
      description: 'Your command center for bookings, earnings, and navigation across GoCareMo.',
      route: dashboard,
      placement: 'bottom',
    },
    {
      target: 'provider-nav',
      title: 'Main navigation',
      description: 'Move between profile, bookings, schedule, services, payouts, and verification.',
      route: dashboard,
      placement: 'bottom',
    },
  ];

  if (!isVerified) {
    steps.push(
      {
        target: 'provider-verification-header',
        title: 'Complete verification',
        description: 'Finish verification so patients can find and book you in search results.',
        route: verification,
        placement: 'bottom',
      },
      {
        target: 'provider-verification-steps',
        title: 'Verification steps',
        description: 'Complete personal info, professional details, and upload ID, licenses, and required documents for admin review.',
        route: verification,
        placement: 'bottom',
      },
    );
  }

  if (isVerified) {
    steps.push(
      {
        target: 'provider-services-header',
        title: 'My Services',
        description: 'Create the services patients can book, including rates and service mode.',
        route: services,
        placement: 'bottom',
      },
      {
        target: 'provider-services-add',
        title: 'Add a service',
        description: 'List home visits, clinic consults, and specialties you offer.',
        route: services,
        placement: 'left',
      },
      {
        target: 'provider-services-list',
        title: 'Manage services',
        description: 'Edit pricing, availability, descriptions, and activate or deactivate services.',
        route: services,
        placement: 'top',
      },
      {
        target: 'provider-schedule-header',
        title: 'My Schedule',
        description: 'Control when patients can book you and review upcoming appointments.',
        route: schedule,
        placement: 'bottom',
      },
      {
        target: 'provider-schedule-tabs',
        title: 'Calendar and settings',
        description: 'View upcoming appointments on the calendar or open Schedule Settings to set your weekly availability.',
        route: schedule,
        placement: 'bottom',
      },
      {
        target: 'provider-bookings-header',
        title: 'Booking requests',
        description: 'Review new requests and manage active appointments.',
        route: bookings,
        placement: 'bottom',
      },
      {
        target: 'provider-bookings-list',
        title: 'Appointment workflow',
        description: 'Confirm visits, mark in progress, submit proof of service, and track status.',
        route: bookings,
        placement: 'top',
      },
      {
        target: 'provider-reviews-header',
        title: 'Patient reviews',
        description: 'See ratings and feedback to build trust with future patients.',
        route: reviews,
        placement: 'bottom',
      },
      {
        target: 'provider-reviews-summary',
        title: 'Rating summary',
        description: 'Track your average rating and review breakdown at a glance.',
        route: reviews,
        placement: 'bottom',
      },
      {
        target: 'provider-payouts-header',
        title: 'Payouts',
        description: 'Monitor weekly earnings and payout cycles from completed bookings.',
        route: payouts,
        placement: 'bottom',
      },
      {
        target: 'provider-payouts-summary',
        title: 'Earnings summary',
        description: 'View pending, released, and total payout amounts for your account.',
        route: payouts,
        placement: 'bottom',
      },
    );
  }

  steps.push(
    {
      target: 'provider-profile-header',
      title: 'Provider profile',
      description: 'Keep your public profile, contact details, and professional bio up to date.',
      route: profile,
      placement: 'bottom',
    },
    {
      target: 'provider-profile-payout-account',
      title: 'Payout account',
      description: 'Add or update the bank or e-wallet account where GoCareMo sends your earnings.',
      route: profile,
      placement: 'top',
    },
  );

  return steps;
}

export function getSearchTourSteps(): GuidedTourStep[] {
  return SEARCH_TOUR_STEPS;
}

// Backward-compatible helpers
export function getPatientDashboardTourSteps(): GuidedTourStep[] {
  return buildPatientFullTour({}).slice(0, 4);
}

export function getProviderDashboardTour(includeVerification: boolean): GuidedTourStep[] {
  return buildProviderFullTour({ providerVerified: !includeVerification }).slice(0, includeVerification ? 3 : 2);
}
